/* import __COLOCATED_TEMPLATE__ from './deploy.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Tab } from 'embercom/components/standalone/common/tab-control';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';

export interface Args {
  activeStep: 'integrate' | 'workflow' | 'go-live';
  openSection: string | null;
  setSection?: (section: string | null) => void;
}

export default class Deploy extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @tracked selectedStep: Tab;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.selectedStep =
      this.deploySteps.find((step) => step.value === args.activeStep) ?? this.deploySteps[0];
  }

  get freshdeskConfig() {
    return this.finStandaloneService.freshdeskConfig;
  }

  get isWorkflowStepComplete() {
    return (
      this.freshdeskConfig?.isEnabledForTickets ||
      (this.freshdeskConfig?.ticketsIntegration?.workflowRulesetId &&
        this.selectedStep?.stepNumber > 2)
    );
  }

  get deploySteps(): Array<Tab> {
    return [
      {
        stepNumber: 1,
        label: this.intl.t('standalone.freshdesk.deploy.tickets.steps.integrate'),
        value: 'integrate',
        path: 'apps.app.standalone.deploy.freshdesk.tickets.integrate',
        isCompleted: this.freshdeskConfig?.isConnected || false,
      },
      {
        stepNumber: 2,
        label: this.intl.t('standalone.freshdesk.deploy.tickets.steps.workflow'),
        value: 'workflow',
        path: 'apps.app.standalone.deploy.freshdesk.tickets.workflow',
        isCompleted: !!this.isWorkflowStepComplete,
      },
      {
        stepNumber: 3,
        label: this.intl.t('standalone.freshdesk.deploy.tickets.steps.go-live'),
        value: 'go-live',
        path: 'apps.app.standalone.deploy.freshdesk.tickets.go-live',
        isCompleted: this.freshdeskConfig?.isEnabledForTickets || false,
      },
    ];
  }

  @action
  setStep(stepValue: string) {
    let step = this.deploySteps.find((step) => step.value === stepValue);
    if (step) {
      this.selectedStep = step;
      if (step.path) {
        this.router.transitionTo(step.path);
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Freshdesk::Tickets::Deploy': typeof Deploy;
    'standalone/freshdesk/tickets/deploy': typeof Deploy;
  }
}
